/**
 * styleGroupEditorFactories — Bridge between ButtonStyleSections values/onChange
 * and the object-level editor components (Shadow, Effects, Icon).
 *
 * Same pattern as gradientEditorFactory.tsx — factory functions create wrapper
 * components that read from the flat values map and write back via onChange(path, value).
 */

import React, { useCallback } from 'react';
import type { ShadowStyle } from '@segmently/schema-models/v2';
import { ShadowEditor } from './ShadowEditor';
import { EffectsEditor } from './EffectsEditor';
import { IconEditor } from './IconEditor';

interface StyleGroupWrapperProps {
  values: Record<string, unknown>;
  onChange: (path: string, value: unknown) => void;
  disabled?: boolean;
  locale?: string;
  /** Optional upload handler for icon files (passed through from ButtonStyleSections) */
  onIconUpload?: (file: File) => Promise<string>;
}

// ============================================
// Shadow Editor Factory
// ============================================

/**
 * Create a ShadowEditor wrapper for ButtonStyleSections.
 * Reads/writes `{basePath}.appearance.shadow` as a ShadowStyle object.
 *
 * @param basePath - TapTarget base path (e.g., 'content.actionBar.primary')
 */
export function createShadowEditorComponent(
  basePath: string,
): React.ComponentType<StyleGroupWrapperProps> {
  const shadowPath = `${basePath}.appearance.shadow`;

  const ShadowEditorWrapper: React.FC<StyleGroupWrapperProps> = ({
    values,
    onChange,
    disabled,
  }) => {
    const shadow = (values[shadowPath] as ShadowStyle | null) ?? null;

    const handleChange = useCallback(
      (newShadow: ShadowStyle | null) => {
        onChange(shadowPath, newShadow);
      },
      [onChange],
    );

    return (
      <ShadowEditor
        value={shadow}
        onChange={handleChange}
        disabled={disabled}
      />
    );
  };

  ShadowEditorWrapper.displayName = `ShadowEditorWrapper(${basePath})`;
  return ShadowEditorWrapper;
}

// ============================================
// Effects Editor Factory
// ============================================

/**
 * Create an EffectsEditor wrapper for ButtonStyleSections.
 * Reads/writes two separate paths:
 * - `{basePath}.appearance.opacity` (number | null)
 * - `{basePath}.appearance.animation.kind` (string | null)
 *
 * @param basePath - TapTarget base path (e.g., 'content.actionBar.primary')
 */
export function createEffectsEditorComponent(
  basePath: string,
): React.ComponentType<StyleGroupWrapperProps> {
  const opacityPath = `${basePath}.appearance.opacity`;
  const animationKindPath = `${basePath}.appearance.animation.kind`;
  const animationObjectPath = `${basePath}.appearance.animation`;
  // v2.17 ElementAnimation objects — written whole ({ preset } | null) so the
  // runtime's new-over-legacy priority engages cleanly
  const enterPresetPath = `${basePath}.appearance.enterAnimation.preset`;
  const enterObjectPath = `${basePath}.appearance.enterAnimation`;
  const tapPresetPath = `${basePath}.appearance.tapAnimation.preset`;
  const tapObjectPath = `${basePath}.appearance.tapAnimation`;
  const idlePresetPath = `${basePath}.appearance.idleAnimation.preset`;
  const idleObjectPath = `${basePath}.appearance.idleAnimation`;

  const EffectsEditorWrapper: React.FC<StyleGroupWrapperProps> = ({
    values,
    onChange,
    disabled,
  }) => {
    const opacity = values[opacityPath] as number | null | undefined;
    const animationKind = values[animationKindPath] as string | null | undefined;
    const enterPreset = values[enterPresetPath] as string | null | undefined;
    const tapPreset = values[tapPresetPath] as string | null | undefined;
    const idlePreset = values[idlePresetPath] as string | null | undefined;

    const handleOpacityChange = useCallback(
      (value: number | null) => {
        onChange(opacityPath, value);
      },
      [onChange],
    );

    const handleAnimationChange = useCallback(
      (value: string | null) => {
        onChange(animationKindPath, value);
      },
      [onChange],
    );

    const handleEnterPresetChange = useCallback(
      (value: string | null) => {
        onChange(enterObjectPath, value ? { preset: value } : null);
      },
      [onChange],
    );

    const handleTapPresetChange = useCallback(
      (value: string | null) => {
        onChange(tapObjectPath, value ? { preset: value } : null);
      },
      [onChange],
    );

    const handleIdlePresetChange = useCallback(
      (value: string | null) => {
        onChange(idleObjectPath, value ? { preset: value } : null);
      },
      [onChange],
    );

    return (
      <EffectsEditor
        opacity={opacity}
        animationKind={animationKind}
        onOpacityChange={handleOpacityChange}
        onAnimationChange={handleAnimationChange}
        enterPreset={enterPreset}
        tapPreset={tapPreset}
        idlePreset={idlePreset}
        onEnterPresetChange={handleEnterPresetChange}
        onTapPresetChange={handleTapPresetChange}
        onIdlePresetChange={handleIdlePresetChange}
        disabled={disabled}
      />
    );
  };

  EffectsEditorWrapper.displayName = `EffectsEditorWrapper(${basePath})`;
  return EffectsEditorWrapper;
}

// ============================================
// Icon Editor Factory
// ============================================

/**
 * Create an IconEditor wrapper for ButtonStyleSections.
 * Reads/writes multiple icon-related paths:
 * - `{basePath}.icon` (LocalizedImage object)
 * - `{basePath}.iconPlacement` (string)
 * - `{basePath}.icon.appearance.scaleMode` (string)
 * - `{basePath}.icon.appearance.dimensions.width` (number)
 * - `{basePath}.icon.appearance.dimensions.height` (number)
 * - `{basePath}.icon.appearance.cornerRadii.uniform` (number)
 *
 * @param basePath - TapTarget base path (e.g., 'content.actionBar.primary')
 */
export function createIconEditorComponent(
  basePath: string,
): React.ComponentType<StyleGroupWrapperProps> {
  const iconPath = `${basePath}.icon`;
  const placementPath = `${basePath}.iconPlacement`;
  const scaleModePath = `${basePath}.icon.appearance.scaleMode`;
  const widthPath = `${basePath}.icon.appearance.dimensions.width`;
  const heightPath = `${basePath}.icon.appearance.dimensions.height`;
  const cornerRadiusPath = `${basePath}.icon.appearance.cornerRadii.uniform`;

  const IconEditorWrapper: React.FC<StyleGroupWrapperProps> = ({
    values,
    onChange,
    disabled,
    locale = 'en',
    onIconUpload,
  }) => {
    const iconValue = values[iconPath] ?? null;
    const placement = values[placementPath] as string | null | undefined;
    const scaleMode = values[scaleModePath] as string | null | undefined;
    const iconWidth = values[widthPath] as number | null | undefined;
    const iconHeight = values[heightPath] as number | null | undefined;
    const cornerRadius = values[cornerRadiusPath] as number | null | undefined;

    // Top-level useCallback declarations — matching Gradient pattern
    const handleIconChange = useCallback(
      (v: unknown) => onChange(iconPath, v),
      [onChange],
    );
    const handlePlacementChange = useCallback(
      (v: string | null) => onChange(placementPath, v),
      [onChange],
    );
    const handleScaleModeChange = useCallback(
      (v: string | null) => onChange(scaleModePath, v),
      [onChange],
    );
    const handleWidthChange = useCallback(
      (v: number | null) => onChange(widthPath, v),
      [onChange],
    );
    const handleHeightChange = useCallback(
      (v: number | null) => onChange(heightPath, v),
      [onChange],
    );
    const handleCornerRadiusChange = useCallback(
      (v: number | null) => onChange(cornerRadiusPath, v),
      [onChange],
    );

    return (
      <IconEditor
        iconValue={iconValue}
        iconPlacement={placement}
        scaleMode={scaleMode}
        iconWidth={iconWidth}
        iconHeight={iconHeight}
        cornerRadius={cornerRadius}
        onIconChange={handleIconChange}
        onPlacementChange={handlePlacementChange}
        onScaleModeChange={handleScaleModeChange}
        onWidthChange={handleWidthChange}
        onHeightChange={handleHeightChange}
        onCornerRadiusChange={handleCornerRadiusChange}
        onIconUpload={onIconUpload}
        locale={locale}
        disabled={disabled}
      />
    );
  };

  IconEditorWrapper.displayName = `IconEditorWrapper(${basePath})`;
  return IconEditorWrapper;
}
